import { Loader2, MessageCircleQuestion, MessageSquarePlus } from 'lucide-react';
import { useCallback, useEffect, useId, useState } from 'react';
import { toast } from 'sonner';
import { createTicket, listTickets } from '@/api/member';
import MemberTicketItem from '@/components/member/MemberTicketItem';
import Pagination from '@/components/member/Pagination';

const SupportTicketsSubpage = () => {
  const uid = useId();
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listTickets(page);
      setData(r);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createTicket(subject.trim(), message.trim());
      toast.success('Ticket submitted');
      setSubject('');
      setMessage('');
      setPage(1);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      toast.error(`Submit failed: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="subpage-support">
      <section className="sc-glass-strong rounded-2xl p-5 md:p-7">
        <div className="sc-pill">
          <span className="sc-dot" />
          Open A Support Ticket
        </div>
        <h3 className="font-display font-bold text-xl md:text-2xl mt-3">
          Get in touch with <span className="sc-gold-text">administration</span>
        </h3>
        <form onSubmit={submit} className="mt-5 grid grid-cols-1 gap-4">
          <div>
            <label htmlFor={`${uid}-subject`} className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">
              Subject
            </label>
            <input
              id={`${uid}-subject`}
              type="text"
              maxLength={140}
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Brief subject line…"
              className="w-full mt-1.5 px-4 py-3 rounded-xl bg-[rgba(17,17,17,0.6)] border border-[rgba(255,182,18,0.20)] text-[var(--sc-text)] placeholder:text-[var(--sc-text-mute)] focus:outline-none focus:border-[rgba(255,182,18,0.55)]"
              data-testid="ticket-subject"
            />
          </div>
          <div>
            <label htmlFor={`${uid}-message`} className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">
              Message
            </label>
            <textarea
              id={`${uid}-message`}
              required
              minLength={1}
              maxLength={4000}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              placeholder="Describe your issue. Support replies show up in your ticket below, and you can reply back."
              className="w-full mt-1.5 px-4 py-3 rounded-xl bg-[rgba(17,17,17,0.6)] border border-[rgba(255,182,18,0.20)] text-[var(--sc-text)] placeholder:text-[var(--sc-text-mute)] focus:outline-none focus:border-[rgba(255,182,18,0.55)] resize-y"
              data-testid="ticket-message"
            />
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={submitting} className="sc-btn-white" data-testid="ticket-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquarePlus className="w-4 h-4" />}
              Submit Ticket
            </button>
          </div>
        </form>
      </section>

      <section className="sc-glass-strong rounded-2xl p-5 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="sc-pill">
              <span className="sc-dot" />
              Your Tickets
            </div>
            <h3 className="font-display font-bold text-xl md:text-2xl mt-3">Conversation History</h3>
          </div>
          <div className="hidden md:flex items-center gap-2 text-[var(--sc-text-mute)] text-[11px] uppercase tracking-[0.22em]">
            <MessageCircleQuestion className="w-3.5 h-3.5" />
            {data?.total ?? 0} total
          </div>
        </div>

        {loading ? (
          <div className="p-8 flex items-center justify-center text-[var(--sc-text-dim)] text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        ) : !data?.items?.length ? (
          <div className="p-10 text-center text-[var(--sc-text-dim)] text-sm">No tickets yet. Submit one above to start a conversation.</div>
        ) : (
          <ul className="divide-y divide-[rgba(255,182,18,0.06)]">
            {data.items.map((t) => (
              <MemberTicketItem key={t.ticket_id} ticket={t} onReplied={load} />
            ))}
          </ul>
        )}

        {data && <Pagination page={data.page} totalPages={data.total_pages} total={data.total} onChange={setPage} />}
      </section>
    </div>
  );
};

export default SupportTicketsSubpage;
